import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, MoveLeft, ShieldCheck } from "lucide-react";

export default function AdminLogin() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      nav("/admin");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      toast.error(typeof msg === 'string' ? msg : "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-cream-100 flex" data-testid="admin-login-page">
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img src="https://customer-assets.emergentagent.com/job_ef4fdd3f-901c-4603-a956-907996740397/artifacts/wbnpn6y2_Slide_02.png"
             alt="Naljuri Inn" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-forest-900/70" />
        <div className="absolute inset-0 grain-overlay" />
        <div className="relative z-10 p-16 flex flex-col justify-between text-cream-100">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-cream-100/85 hover:text-gold-400" data-testid="back-to-site">
            <MoveLeft className="w-4 h-4" /> Back to site
          </Link>
          <div>
            <p className="eyebrow text-gold-400 flex items-center gap-3">
              <span className="inline-block w-7 h-px bg-gold-400/80"></span>
              Naljuri Inn · Admin
            </p>
            <h1 className="mt-4 text-5xl font-display leading-tight">The quiet operations<br/>of a basecamp.</h1>
            <p className="mt-6 text-cream-100/75 max-w-md">Manage rooms, track inquiries, and keep the river-side running smoothly.</p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="admin-login-form">
          <div className="flex items-center gap-2 text-gold-600 mb-3"><ShieldCheck className="w-5 h-5"/><span className="text-xs uppercase tracking-widest2">Admin access</span></div>
          <h2 className="text-3xl font-display text-forest-700">Sign in to continue</h2>
          <p className="mt-2 text-sm text-slate-warm">Authorised personnel only.</p>

          <div className="mt-10 space-y-5">
            <label className="block">
              <span className="block text-[11px] uppercase tracking-widest2 text-gold-600 mb-2">Email</span>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                     data-testid="admin-email-input"
                     className="w-full bg-white border border-forest-100 px-4 py-3 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gold-600/40 focus:border-gold-600" />
            </label>
            <label className="block">
              <span className="block text-[11px] uppercase tracking-widest2 text-gold-600 mb-2">Password</span>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                     data-testid="admin-password-input"
                     className="w-full bg-white border border-forest-100 px-4 py-3 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-gold-600/40 focus:border-gold-600" />
            </label>
          </div>

          <button type="submit" disabled={loading} data-testid="admin-login-submit"
                  className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-forest-700 text-cream-100 py-3.5 rounded-full text-sm hover:bg-forest-800 transition-colors disabled:opacity-60 border border-gold-600/40">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Sign in
          </button>
          <Link to="/" className="block mt-6 text-xs text-slate-warm hover:text-gold-700 text-center lg:hidden">← Back to site</Link>
        </form>
      </div>
    </div>
  );
}
